import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import {
  DueDiligenceItem,
  IDueDiligenceItem,
} from '@app/models/DueDiligenceItem'
import { bindFunctions } from '@app/utils/class'
import { formatDate } from '@app/utils/formatting'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Link } from 'react-router-dom'
import { Button, Popover, PopoverBody, PopoverHeader } from 'reactstrap'

export interface IPipelineItem {
  children: IPipelineItem[]
  item: IDueDiligenceItem
}

export interface IDevelopmentPipelineItemProps {
  item: IPipelineItem
  deleteItem: (id: string) => void
  editItem: (item: DueDiligenceItem) => void
}

interface IDevelopmentPipelineItemState {
  showAssignedTo: boolean
  showAttachments: boolean
  showConfirmDelete: boolean
}

export class DevelopmentPipelineItem extends React.PureComponent<
  IDevelopmentPipelineItemProps,
  IDevelopmentPipelineItemState
> {
  constructor(props: IDevelopmentPipelineItemProps) {
    super(props)
    this.state = {
      showAssignedTo: false,
      showAttachments: false,
      showConfirmDelete: false,
    }
    bindFunctions(this)
  }

  public render(): JSX.Element {
    const completeIcon: [IconPrefix, IconName] = this.props.item.item.isComplete
      ? ['far', 'check']
      : ['far', 'times']

    return (
      <div>
        <div className='development-pipeline-item'>
          <div
            className='p-2'
            title={this.props.item.item.isComplete ? 'Complete' : 'Incomplete'}
          >
            <FontAwesomeIcon
              className={
                this.props.item.item.isComplete ? 'text-primary' : 'text-danger'
              }
              icon={completeIcon}
              fixedWidth
            />
          </div>
          <div className='flex-grow-1 p-2'>
            <div className='font-weight-semi-bold' title={this._getItemTitle()}>
              {this._getItemTitle()}
            </div>
            {this.props.item.item.dueDate && (
              <div
                className='small'
                title={`Due on ${formatDate(
                  this.props.item.item.dueDate,
                  'MM/DD/YYYY',
                )}`}
              >
                <FontAwesomeIcon
                  className='mr-2'
                  icon={['fal', 'calendar-check']}
                  fixedWidth
                />
                {formatDate(this.props.item.item.dueDate, 'MM/DD/YYYY')}
              </div>
            )}
          </div>
          <div>
            {this.props.item.item.assignedTo.size > 0 && (
              <Button
                color='link'
                id={`assignedTo-${this.props.item.item.id}`}
                onClick={this._toggleAssignedTo}
                title='Assigned Connections'
              >
                <FontAwesomeIcon icon={['fal', 'user']} fixedWidth />
                <sub>{this.props.item.item.assignedTo.size}</sub>
                <Popover
                  isOpen={this.state.showAssignedTo}
                  placement='top'
                  target={`assignedTo-${this.props.item.item.id}`}
                  toggle={this._toggleAssignedTo}
                >
                  <PopoverHeader>Assigned Connections</PopoverHeader>
                  <PopoverBody>
                    {this.props.item.item.assignedTo.toArray().map((assigned) => (
                      <div key={assigned.contactId}>
                        {assigned.name}
                        <Link
                          className='ml-2'
                          to={`/connection/${assigned.contactId}`}
                          target='_blank'
                          rel='noreferrer'
                          title='View profile'
                        >
                          <FontAwesomeIcon
                            icon={['fal', 'external-link-alt']}
                            fixedWidth
                          />
                        </Link>
                      </div>
                    ))}
                  </PopoverBody>
                </Popover>
              </Button>
            )}
            {this.props.item.item.attachments.size > 0 && (
              <Button
                color='link'
                id={`attachments-${this.props.item.item.id}`}
                onClick={this._toggleAttachments}
                title='Attachments'
              >
                <FontAwesomeIcon icon={['fal', 'paperclip']} fixedWidth />
                <sub>{this.props.item.item.attachments.size}</sub>
                <Popover
                  isOpen={this.state.showAttachments}
                  placement='top'
                  target={`attachments-${this.props.item.item.id}`}
                  toggle={this._toggleAttachments}
                >
                  <PopoverHeader>Attachments</PopoverHeader>
                  <PopoverBody>
                    {this.props.item.item.attachments.toArray().map((attach) => (
                      <div key={attach.id}>
                        {attach.title}
                        <a
                          className='ml-2'
                          href={`/api/Files/${attach.id}/Download`}
                          target='_blank'
                          rel='noreferrer'
                          title={`Download ${attach.title}`}
                        >
                          <FontAwesomeIcon
                            icon={['fal', 'download']}
                            fixedWidth
                          />
                        </a>
                      </div>
                    ))}
                  </PopoverBody>
                </Popover>
              </Button>
            )}
          </div>
          <div className='actions'>
            <Button color='link' onClick={this._onEdit}>
              <FontAwesomeIcon icon={['fal', 'pencil']} fixedWidth />
            </Button>
            <Button
              className='text-danger'
              color='link'
              onClick={this._toggleDelete}
            >
              <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
              <DeleteConfirmationModal
                isOpen={this.state.showConfirmDelete}
                itemName={this.props.item.item.title}
                onConfirm={this._onDelete}
                onToggleModal={this._toggleDelete}
              />
            </Button>
          </div>
        </div>
        {this.props.item.children.length > 0 && (
          <div className='ml-4'>
            {this.props.item.children.map((item) => (
              <DevelopmentPipelineItem
                key={item.item.id}
                deleteItem={this.props.deleteItem}
                editItem={this.props.editItem}
                item={item}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  private _getItemTitle() {
    return `${this.props.item.item.title}${
      !this.props.item.item.isPublic ? ' (Private)' : ''
    }`
  }

  private _onDelete() {
    this.props.deleteItem(this.props.item.item.id)
  }

  private _onEdit() {
    this.props.editItem(this.props.item.item as DueDiligenceItem)
  }

  private _toggleAssignedTo() {
    this.setState({ showAssignedTo: !this.state.showAssignedTo })
  }

  private _toggleAttachments() {
    this.setState({ showAttachments: !this.state.showAttachments })
  }

  private _toggleDelete() {
    this.setState({ showConfirmDelete: !this.state.showConfirmDelete })
  }
}
